import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { useCreateCustomerGroup, useCustomerGroupsOverview, useUpdateCustomerGroup } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { CUSTOMER_GROUP_KIND_LABELS } from '../../model/options';
import type { CustomerGroupRequest, CustomerGroupResponse } from '../../model/customerGroupTypes';
import { CustomerGroupFormDialog } from './CustomerGroupFormDialog';
import { CustomerGroupMembersDrawer } from './CustomerGroupMembersDrawer';

export interface CustomerGroupsPanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * The "Customer groups" card (design spec §8.10) — embedded below the existing `ChannelsPanel`
 * (both are price-slot dimensions). The intro caption doubles as the empty state, keeping groups
 * out of a simple retailer's way entirely when the tenant has none.
 *
 * **Contract gaps (disclosed):** no `GET .../customer-groups/{code}` (edit seeds from the row
 * already held by the overview query) and no `DELETE` — the exact archive-only gap price lists hit
 * in WP4.2. An Active `Switch` (the price-list kill-switch precedent) is the reachable terminal
 * state; the design mock's 🗑 delete icon has no backing endpoint and is not rendered.
 */
export function CustomerGroupsPanel({ canManage, onResult }: CustomerGroupsPanelProps) {
  const { t } = useTranslation('catalog');
  const overview = useCustomerGroupsOverview();
  const createMutation = useCreateCustomerGroup();
  const updateMutation = useUpdateCustomerGroup();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerGroupResponse | undefined>();
  const [membersFor, setMembersFor] = useState<CustomerGroupResponse | null>(null);

  const handleSave = (body: CustomerGroupRequest) => {
    if (editing) {
      updateMutation.mutate(
        { code: editing.code, body: { ...body, active: editing.active } },
        {
          onSuccess: () => {
            setFormOpen(false);
            setEditing(undefined);
            onResult('success', t('customerGroupsPanel.saved'));
          },
          onError: (error) => onResult('error', error.message),
        },
      );
      return;
    }
    createMutation.mutate(body, {
      onSuccess: () => {
        setFormOpen(false);
        onResult('success', t('customerGroupsPanel.created'));
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  const toggleActive = (group: CustomerGroupResponse) => {
    updateMutation.mutate(
      {
        code: group.code,
        body: {
          code: group.code,
          name: group.name,
          description: group.description,
          priority: group.priority,
          active: !group.active,
        },
      },
      {
        onSuccess: () =>
          onResult('success', !group.active ? t('customerGroupsPanel.activated') : t('customerGroupsPanel.deactivated')),
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  const createButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddRoundedIcon />}
      disabled={!canManage}
      onClick={() => {
        setEditing(undefined);
        setFormOpen(true);
      }}
    >
      {t('customerGroupsPanel.create')}
    </Button>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Stack spacing={0.5} sx={{ maxWidth: 560 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('customerGroupsPanel.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customerGroupsPanel.intro')}
            </Typography>
          </Stack>
          {canManage ? (
            createButton
          ) : (
            <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
              <span>{createButton}</span>
            </Tooltip>
          )}
        </Stack>

        {overview.isLoading ? (
          <LoadingState label={t('customerGroupsPanel.loading')} fullHeight={false} />
        ) : overview.isError ? (
          <ErrorState message={t('customerGroupsPanel.loadError')} onRetry={overview.refetch} />
        ) : overview.groups.length === 0 ? null : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('customerGroupsPanel.table.name')}</TableCell>
                  <TableCell>{t('customerGroupsPanel.table.code')}</TableCell>
                  <TableCell>{t('customerGroupsPanel.table.kind')}</TableCell>
                  <TableCell align="right">{t('customerGroupsPanel.table.members')}</TableCell>
                  <TableCell>{t('customerGroupsPanel.table.active')}</TableCell>
                  <TableCell align="right">{t('customerGroupsPanel.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.groups.map((group) => {
                  const isDynamic = group.kind === 'DYNAMIC';
                  const rowBusy = updateMutation.isPending && updateMutation.variables?.code === group.code;
                  const toggle = (
                    <Switch
                      size="small"
                      checked={group.active}
                      disabled={!canManage || rowBusy}
                      onChange={() => toggleActive(group)}
                      slotProps={{
                        input: { 'aria-label': t('customerGroupsPanel.activeToggleAria', { name: group.name }) },
                      }}
                    />
                  );
                  const editButton = (
                    <IconButton
                      size="small"
                      aria-label={t('customerGroupsPanel.editAria', { name: group.name })}
                      disabled={!canManage}
                      onClick={() => {
                        setEditing(group);
                        setFormOpen(true);
                      }}
                    >
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  );
                  return (
                    <TableRow key={group.code} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {group.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                          {group.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(CUSTOMER_GROUP_KIND_LABELS[group.kind] ?? group.kind)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {isDynamic ? (
                          <Tooltip title={t('customerGroupsPanel.dynamicMembersTooltip')}>
                            <Typography variant="body2" color="text.secondary" component="span">
                              —
                            </Typography>
                          </Tooltip>
                        ) : overview.memberCountLoadingCodes.has(group.code) ? (
                          <CircularProgress size={14} />
                        ) : (
                          (overview.memberCountByCode.get(group.code) ?? 0)
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {canManage ? (
                          toggle
                        ) : (
                          <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
                            <span>{toggle}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          {canManage ? (
                            editButton
                          ) : (
                            <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
                              <span>{editButton}</span>
                            </Tooltip>
                          )}
                          {isDynamic ? (
                            <Tooltip title={t('customerGroupsPanel.dynamicMembersTooltip')}>
                              <span>
                                <IconButton size="small" disabled aria-label={t('customerGroupsPanel.membersAria', { name: group.name })}>
                                  <GroupRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <IconButton
                              size="small"
                              aria-label={t('customerGroupsPanel.membersAria', { name: group.name })}
                              onClick={() => setMembersFor(group)}
                            >
                              <GroupRoundedIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <CustomerGroupFormDialog
        open={formOpen}
        group={editing}
        pending={createMutation.isPending || updateMutation.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditing(undefined);
        }}
        onSave={handleSave}
      />
      <CustomerGroupMembersDrawer
        open={membersFor !== null}
        group={membersFor ? { code: membersFor.code, name: membersFor.name } : null}
        canManage={canManage}
        onClose={() => setMembersFor(null)}
        onResult={onResult}
      />
    </Card>
  );
}
